import { API } from '@/api/API'
import { groupBy, indexBy, indexItemsById } from '@/helpers/IndexHelpers'
import { TixTime } from '@/TixTime/TixTime'

const dateFormat = 'ddd, MMMM D YYYY'

function byStartDate(a: OrderSession, b: OrderSession) {
  if (a.start_datetime! > b.start_datetime!) return 1
  if (a.start_datetime! < b.start_datetime!) return -1
  return 0
}

function filterUpcomingSessions(sessions: OrderSession[]): OrderSession[] {
  return sessions.filter((session) => new TixTime(session.start_datetime).isAfter()).sort(byStartDate)
}

interface OrderSession extends SearchResult {
  tickets: StandardTicket[]
}

export function fetchOrderSessions(orderId?: string): Promise<OrderSession[]> {
  return API.getUncached<'search_result' | 'ticket'>(`my/order_session`, {
    embed: 'ticket',
    query: { 'ticket.state._in': 'booked,redeemed' },
  }).then((response) => {
    // Filter out gift cards, donations, memberships, admission passes; any search_result without a start time
    // TODO: Extend the API to support this filtering on search_result queries?
    // There is work planned to include these tickets in My Account in future
    const searchResults = response.search_result._data
      .filter((result) => Boolean(result.start_datetime))
      .filter((result) => (orderId ? result.ticket_order_id === orderId : true))

    const tickets: StandardTicket[] = response.ticket._data
      .filter((ticket) => Boolean(ticket.event_session_id))
      .filter((ticket) => (orderId ? ticket.ticket_order_id === orderId : true)) as StandardTicket[]

    const index: Dict<Dict<StandardTicket[]>> = {}
    for (const ticket of tickets) {
      index[ticket.ticket_order_id!] ??= {}
      index[ticket.ticket_order_id!][ticket.event_session_id!] ??= []
      index[ticket.ticket_order_id!][ticket.event_session_id!].push(ticket)
    }

    return searchResults
      .map((item): OrderSession => {
        return {
          ...item,
          tickets: index[item.ticket_order_id]?.[item.event_session_id] ?? [],
        }
      })
      .filter((item) => item.tickets.length > 0)
  })
}

export function getOrderTotalPrice(id: string): Promise<number> {
  // Don't use the "my" prefix for this call
  // "my/ticket_order" doesn't include "ticket_order_fees" in the response
  return API.getUncached<'ticket_order' | 'ticket_order_fees'>(`ticket_order/${id}`).then((response) => {
    return parseFloat(response.ticket_order_fees._data[0].total_after_fees_price)
  })
}

interface OrderDetail {
  templates: EventTemplate[]
  orders: OrderSession[]
  venues: Venue[]
  price?: number
}

export function getMyOrders(): Promise<OrderDetail | null> {
  return fetchOrderSessions().then(fetchOrderDetails)
}

export function getMyOrder(orders: OrderSession[], id: string): Promise<RecentOrder | null> {
  return fetchOrderDetails(orders).then((orderDetails) => {
    if (!orderDetails) return null
    return getOrderTotalPrice(id)
      .then((price) => {
        return { ...orderDetails, price }
      })
      .then(({ orders, venues, templates, price }) => {
        return formatRecentOrders(orders, venues, templates, price)[id]
      })
  })
}

function fetchOrderDetails(orders: OrderSession[]): Promise<OrderDetail | null> {
  if (!orders.length) return Promise.resolve(null)

  const eventIds = orders.map((order) => order.event_template_id)
  return API.getCached<'event_template' | 'venue'>('event_template', {
    query: { 'id._in': eventIds },
    embed: 'venue',
  }).then((response) => {
    return {
      orders: orders.sort((a, b) => {
        if (a.purchased_on < b.purchased_on) return 1
        if (a.purchased_on > b.purchased_on) return -1
        return 0
      }),
      venues: response.venue._data,
      templates: response.event_template._data,
    }
  })
}

export function formatRecentOrders(
  orders: OrderSession[],
  venues: Venue[],
  templates: EventTemplate[],
  price?: number,
): Dict<RecentOrder> {
  const venuesById = indexItemsById(venues)
  const templatesById = indexItemsById(templates)

  const result = {}
  orders.forEach((order) => {
    const id = order.ticket_order_id
    const template = templatesById[order.event_template_id]
    const timezone = venuesById[template?.venue_id]?.timezone
    if (result[id]) {
      result[id].numTickets += order.tickets.length
      result[id].numSessions += 1
    } else {
      result[id] = {
        orderNumber: order.order_number,
        purchaseDate: new TixTime(order.purchased_on, timezone).format(dateFormat),
        numTickets: order.tickets.length,
        numSessions: 1,
        ticketOrderId: id,
        totalPrice: price ?? null,
      }
    }
  })
  return result
}

export function getMyUpcomingSessions(): Promise<SessionRow[] | null> {
  return fetchOrderSessions().then((sessions) => {
    const upcoming = filterUpcomingSessions(sessions)
    return upcoming.length > 0 ? getSessionDetails(upcoming) : null
  })
}

export function getMySession(id: string): Promise<SessionRow> {
  return fetchOrderSessions()
    .then((sessions) => {
      const result = sessions.filter((session) => session.event_session_id === id)
      return getSessionDetails(result)
    })
    .then((rows) => rows[0])
}

export function getSessionDetails(sessions: OrderSession[]): Promise<SessionRow[]> {
  const eventIds = sessions.map((session) => session.event_template_id)

  return API.getCached<'event_template' | 'venue' | 'meta'>('event_template', {
    query: { 'id._in': eventIds.join(',') },
    embed: 'meta,venue',
  }).then((response) => {
    const profileImages = indexBy(
      'resource_id',
      response.meta._data.filter((metaItem) => {
        return metaItem.metakey === 'image_profile' && metaItem.resource === 'event_template'
      }),
    )
    const venues = indexItemsById(response.venue._data)
    const templates = indexItemsById(response.event_template._data)

    const result: Dict<SessionRow> = {}
    for (const session of sessions) {
      const sessionId = session.event_session_id
      if (result[sessionId]) {
        result[sessionId].ticketQuantity += session.tickets.length
        result[sessionId].orderIds.push(session.ticket_order_id)
      } else {
        const template = templates[session.event_template_id]
        const timezone = venues[template.venue_id].timezone
        const start = new TixTime(session.start_datetime, timezone)

        result[sessionId] = {
          name: session.event_name,
          id: session.event_session_id,
          date: start.format(dateFormat),
          time: start.format('LT'),
          ticketQuantity: session.tickets.length,
          image: profileImages[session.event_template_id]?.value,
          orderIds: [session.ticket_order_id],
          isUpcoming: start.isAfter(),
        }
      }
    }
    return Object.values(result)
  })
}

interface TicketOrderResponse {
  tickets: TicketRow[]
  orders: TicketOrder[]
}

export function fetchTicketOrders(session: SessionRow): Promise<TicketOrderResponse> {
  return API.getUncached<'ticket_order' | 'ticket_group' | 'ticket_type' | 'ticket'>(`my/ticket_order`, {
    query: { 'id._in': session.orderIds, 'ticket.state._in': 'booked,redeemed' },
    embed: 'ticket,ticket_type,ticket_group',
  }).then((response) => {
    const groups = indexItemsById(response.ticket_group._data)
    const types = indexItemsById(response.ticket_type._data)
    const tickets = groupBy('ticket_order_id', response.ticket._data)

    const result: TicketOrderResponse = {
      tickets: [],
      orders: [],
    }

    response.ticket_order._data.forEach((order) => {
      const sessionTickets: TicketRow[] = tickets[order.id]
        .filter((ticket) => ticket.event_session_id === session.id)
        .map((ticket) => ({
          group: groups[ticket.ticket_group_id].name,
          type: types[ticket.ticket_type_id].name,
          code: ticket.scan_code,
        }))
      result.tickets = result.tickets.concat(sessionTickets)
      result.orders = result.orders.concat(order)
    })
    return result
  })
}
